import { TextInput } from '@any-ui-react/core';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { DatePicker } from '@/common/components/shared/date-picker/DatePicker';
import { EmployeeIdTypeSelectSingle } from '@/common/components/shared/select/employee-id-type/EmployeeIdTypeSelect';
import { useEmployeeDetailContext } from '@/common/contexts/EmployeeDetailContext';
import { useEmployeeAccess } from '@/common/hooks/employees/useEmployeeAccess';
import type { EmployeePersonalInformationFormDataType } from '@/common/types';
import { DateUtils } from '@/common/utils';

export const IdentificationFormFields = () => {
  const {
    formState: { errors },
    register,
    setValue,
    control,
  } = useFormContext<EmployeePersonalInformationFormDataType>();

  const idIssuedDate = useWatch({ control, name: 'idIssuedDate' });
  const idExpiryDate = useWatch({ control, name: 'idExpiryDate' });
  const idType = useWatch({ control, name: 'idType' });

  const { employeeDetail } = useEmployeeDetailContext();
  const isNewRecord = !employeeDetail.id;
  const { hasEmployeePersonalInformationEditAccess } = useEmployeeAccess();
  const isEditable = hasEmployeePersonalInformationEditAccess || isNewRecord;

  const { text: idIssuedDateText, isFuture: idIssuedDateIsFuture } = useMemo(
    () => DateUtils.getDifferenceString(idIssuedDate),
    [idIssuedDate],
  );
  const { text: idExpiryDateText, isFuture: idExpiryDateIsFuture } = useMemo(
    () => DateUtils.getDifferenceString(idExpiryDate),
    [idExpiryDate],
  );

  return (
    <div className="mb-6">
      <div className="mb-4 font-semibold">ID</div>
      <fieldset className="form-fieldset">
        <EmployeeIdTypeSelectSingle
          className="col-span-2 lg:col-span-1"
          disabled={!isEditable}
          error={errors.idType?.message}
          label="Identification type"
          placeholder="Select Identification"
          value={idType}
          onChange={value => {
            setValue('idType', value?.value ?? null);
          }}
        />

        <TextInput
          {...register('idNumber')}
          autoComplete="off"
          className="col-span-2 lg:col-span-1"
          data-1p-ignore={true}
          disabled={!isEditable}
          label="ID Number"
          placeholder="ID Number"
        />

        <TextInput
          {...register('idIssuedPlace')}
          autoComplete="off"
          className="col-span-2 lg:col-span-1"
          data-1p-ignore={true}
          disabled={!isEditable}
          label="ID Issued Place"
          placeholder="ID Issued Place"
        />

        <div className="col-span-2 lg:col-span-1">
          <DatePicker
            clearable={true}
            disabled={!isEditable}
            error={errors.idIssuedDate?.message}
            label="ID Issued Date"
            placeholder="yyyy-MM-dd"
            value={idIssuedDate || ''}
            onChange={value => {
              setValue('idIssuedDate', value ? DateUtils.formatDate(value) : null, {
                shouldDirty: true,
              });
            }}
          />
          <div className="text-gray-7 text-xs ml-3">
            {idIssuedDateText}
            {!idIssuedDateIsFuture ? ' ago' : ''}
          </div>
        </div>

        <div className="col-span-2 lg:col-span-1">
          <DatePicker
            clearable={true}
            disabled={!isEditable}
            error={errors.idExpiryDate?.message}
            label="ID Expiry Date"
            placeholder="yyyy-MM-dd"
            value={idExpiryDate || ''}
            onChange={value => {
              setValue('idExpiryDate', value ? DateUtils.formatDate(value) : null, {
                shouldDirty: true,
              });
            }}
          />
          <div className="text-gray-7 text-xs ml-3">
            {idExpiryDateText}
            {!idExpiryDateIsFuture ? ' ago' : ''}
          </div>
        </div>
      </fieldset>
    </div>
  );
};
